import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { Icon, TextButton } from '@amzn/storm-ui';
import CarouselProgressPill from './CarouselProgressPill';
import { CarouselMetricsPublisher } from '../../utils/metrics';

const fadeInFrames = keyframes`
  0% { opacity:0; }
  75% { opacity:0; }
  100% { opacity:1; }
`;

const CarouselContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;
CarouselContainer.displayName = 'CarouselContainer';

const CarouselProgressPillContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: ${({ theme }) => theme.spacing.xxsmall.default} 0;
  padding-top: 2px;
`;
CarouselProgressPillContainer.displayName = 'CarouselProgressPillContainer';

const FadeInContainer = styled.div`
  animation: ${fadeInFrames} 3s;
  position: relative;
`;
FadeInContainer.displayName = 'FadeInContainer';

const ProgressFadeInContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xxlarge.default};
`;

const CarouselSlideContainer = styled.div`
  /* Fall back to auiblue background if linear-gradient isn't supported by the browser */
  background: ${({ theme }) => theme.color.blue[700]};
  background: linear-gradient(45deg, #0e5a65, #008787, #38b293);
  flex: 1 1 auto;
  padding: 0px 74px;
`;
CarouselSlideContainer.displayName = 'CarouselSlideContainer';

const PlayPauseContainer = styled.div`
  margin-right: 16px;
  text-align: center;
`;

// Styled components for the left and right arrows on the carousel
const ArrowButton = styled(TextButton)`
  color: ${({ theme }) => theme.color.gray[0]};
  position: absolute;
  top: 50%;
  width: 24px;
  height: 24px;

  &:hover {
    color: ${({ theme }) => theme.color.gray[0]};
  }

  &:focus {
    outline: 2px solid #fff;
  }
`;

const PlayPauseButton = styled(TextButton)`
  color: ${({ theme }) => theme.color.gray[0]};
  &:hover {
    color: ${({ theme }) => theme.color.gray[0]};
  }
`;

const PreviousButton = styled(ArrowButton)`
  left: -44px;
`;
PreviousButton.displayName = 'PreviousButton';

const NextButton = styled(ArrowButton)`
  right: -44px;
`;
NextButton.displayName = 'NextButton';

export default class Carousel extends PureComponent {
  TIME_INTERVAL = 100;

  constructor(props) {
    super(props);

    this.canRestartAutoPlay = true;
    this.timePlayed = 0;

    this.state = {
      activeIndex: 0,
      isPlaying: props.autoPlay,
      timeElapsed: 0,
      isHovered: false,
    };
  }

  componentDidMount() {
    const { stopOnHover, autoPlay } = this.props;

    if (autoPlay) {
      const carouselWrapper = this.carouselWrapperRef;

      if (stopOnHover && carouselWrapper) {
        carouselWrapper.addEventListener('mouseenter', () => {
          this.setState({ isHovered: true }, () => this.stopTimer());
        });
        carouselWrapper.addEventListener('mouseleave', () => {
          this.setState({ isHovered: false }, () => this.startTimer());
        });
      }

      setTimeout(() => {
        this.play();
      }, 3500);
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  setCarouselWrapperRef = (node) => {
    this.carouselWrapperRef = node;
  };

  goToSlide = (event, index) => {
    CarouselMetricsPublisher.publishCounterMonitor(`goToSlide-${index}`, 1);
    if (event) {
      this.stop();
    }

    const { children } = this.props;
    if (index >= 0 && index < children.length) {
      this.setState({
        activeIndex: index,
        timeElapsed: 0,
        isPlaying: this.state.isPlaying,
      });
    }
  };

  nextSlide = (event) => {
    CarouselMetricsPublisher.publishCounterMonitor(`nextSlide`, 1);
    if (event) {
      this.stop();
    }

    const { activeIndex } = this.state;
    const { children } = this.props;

    let newIndex = activeIndex + 1;
    if (newIndex >= children.length) {
      newIndex = children.length - 1;
      this.stop();
    }

    this.setState({
      activeIndex: newIndex,
      timeElapsed: 0,
      isPlaying: this.state.isPlaying,
    });
  };

  previousSlide = (event) => {
    CarouselMetricsPublisher.publishCounterMonitor(`previousSlide`, 1);
    if (event) {
      this.stop();
    }

    const { activeIndex } = this.state;
    let newIndex = activeIndex - 1;
    if (newIndex < 0) {
      newIndex = 0;
    }

    this.setState({
      activeIndex: newIndex,
      timeElapsed: 0,
      isPlaying: this.state.isPlaying,
    });
  };

  startTimer = () => {
    if (this.state.isPlaying) {
      this.timer = setInterval(() => {
        if (
          this.state.timeElapsed === this.slideDuration &&
          this.state.isPlaying
        ) {
          this.nextSlide();
        } else {
          this.setState((prevState) => {
            const timeElapsed = prevState.timeElapsed + this.TIME_INTERVAL;
            return {
              timeElapsed:
                timeElapsed > this.slideDuration
                  ? this.slideDuration
                  : timeElapsed,
            };
          });
        }
      }, this.TIME_INTERVAL);
    }
  };

  stopTimer = () => {
    if (this.state.isPlaying) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  };

  play = () => {
    CarouselMetricsPublisher.publishCounterMonitor(`start`, 1);
    this.setState(
      {
        isPlaying: true,
      },
      this.startTimer,
    );
  };

  stop = () => {
    CarouselMetricsPublisher.publishCounterMonitor(`stop`, 1);
    this.stopTimer();

    this.setState({
      isPlaying: false,
      timeElapsed: 0,
    });
  };

  render() {
    const { ariaLabels, children } = this.props;
    const { activeIndex, isPlaying, timeElapsed, isHovered } = this.state;

    const progressComponents = [];
    const slides = [];
    React.Children.forEach(children, (child, index) => {
      const isActive = index === activeIndex;
      const { duration } = child.props;
      let value = 0;

      if (isActive) {
        this.slideDuration = child.props.duration;
        value = timeElapsed;
      }

      // Slides before the active slide should show full progress bar
      if (index < activeIndex) {
        value = duration;
      }

      // Disabled no array index keys here because the order is actually an important and
      // distinguishable piece of data for a Carousel. Because we don't necessarily know the
      // nature of the children, index becomes a means for us to distinguish in the right way.
      const progressPill = (
        <CarouselProgressPill
          // eslint-disable-next-line react/no-array-index-key
          key={`progress-pill-${index}`}
          isActive={isActive}
          isPlaying={isPlaying}
          onClick={(event) => this.goToSlide(event, index)}
          max={duration}
          value={value}
          ariaLabel={ariaLabels.progressPillAriaLabels[index]}
        />
      );

      const slide = React.cloneElement(child, { isActive });

      progressComponents.push(progressPill);
      slides.push(slide);
    });

    const isPlayingNow = isHovered ? false : isPlaying;

    const pageDirection = document.documentElement.dir;
    const nextIconType = pageDirection !== 'rtl' ? 'left' : 'right';
    const prevIconType = pageDirection !== 'rtl' ? 'right' : 'left';

    return (
      <CarouselContainer>
        <CarouselSlideContainer>
          <FadeInContainer ref={this.setCarouselWrapperRef}>
            {activeIndex - 1 >= 0 && (
              <PreviousButton onClick={this.previousSlide}>
                <Icon
                  type={`chevron-${nextIconType}`}
                  size="2x"
                  aria-label={ariaLabels.previous}
                />
              </PreviousButton>
            )}
            {slides}
            {activeIndex + 1 < children.length && (
              <NextButton onClick={this.nextSlide}>
                <Icon
                  type={`chevron-${prevIconType}`}
                  size="2x"
                  aria-label={ariaLabels.next}
                />
              </NextButton>
            )}
          </FadeInContainer>
          <ProgressFadeInContainer>
            <PlayPauseContainer>
              <PlayPauseButton onClick={isPlaying ? this.stop : this.play}>
                <Icon
                  type={isPlayingNow ? 'pause-circle' : 'play-circle'}
                  size="large"
                  aria-label={isPlayingNow ? ariaLabels.pause : ariaLabels.play}
                />
              </PlayPauseButton>
            </PlayPauseContainer>
            <CarouselProgressPillContainer>
              {progressComponents}
            </CarouselProgressPillContainer>
          </ProgressFadeInContainer>
        </CarouselSlideContainer>
      </CarouselContainer>
    );
  }
}

Carousel.propTypes = {
  // CarouselSlide Nodes
  children: PropTypes.node.isRequired,
  // Controls whether the Carousel should automatically advance to the next slides
  autoPlay: PropTypes.bool,
  // Controls whether the Carousel should stop advancing to the next slide if it's content is
  // hovered over. This gives the user time to read before moving forward.
  stopOnHover: PropTypes.bool,
  ariaLabels: PropTypes.shape({
    previous: PropTypes.string,
    next: PropTypes.string,
    play: PropTypes.string,
    pause: PropTypes.string,
  }),
};

Carousel.defaultProps = {
  autoPlay: false,
  stopOnHover: true,
  ariaLabels: {
    previous: 'previous',
    next: 'next',
    play: 'play',
    pause: 'pause',
  },
};
